
import React, { useState, useRef } from 'react';
import {
  FileText, Calendar, ShieldCheck, Home, ArrowLeft,
  Upload, Camera, Video, Plus, Save, Printer, X, Eye,
  AlertTriangle, DollarSign, PenTool, CheckCircle2
} from 'lucide-react';

export default function RSInspection() {
  const [status, setStatus] = useState('OPEN');

  const [uploadedMedia, setUploadedMedia] = useState({
    Wall: [], Ceiling: [], Floor: [], WashRoom: [], KitchenCabinet: [], Door: []
  });

  // Category list mirroring your original right panel layout
  const areas = [
    { id: 'Wall', label: 'Wall / Paint Condition' },
    { id: 'Ceiling', label: 'Ceiling & Lighting' },
    { id: 'Floor', label: 'Flooring / Tiles' },
    { id: 'WashRoom', label: 'Wash Room & Plumbing' },
    { id: 'KitchenCabinet', label: 'Kitchen Cabinets & Counters' },
    { id: 'Door', label: 'Doors, Windows & Locks' }
  ];

  // Handle mock media uploads
  const handleMediaUpload = (areaId, type) => {
    const dummyUrl = type === 'video'
      ? 'https://www.w3schools.com/html/mov_bbb.mp4'
      : 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=400';

    setUploadedMedia(prev => ({
      ...prev,
      [areaId]: [...prev[areaId], { type, url: dummyUrl, id: Date.now() }]
    }));
  };

  const removeMedia = (areaId, mediaId) => {
    setUploadedMedia(prev => ({
      ...prev,
      [areaId]: prev[areaId].filter(m => m.id !== mediaId)
    }));
  };

  return (
    <div className="min-h-screen text-slate-800 font-sans antialiased flex flex-col">

      <header className="text-white h-16 min-h-16 flex items-center justify-between px-6 ">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight"> Property Inspection</h1>

        <div className=" mx-auto flex flex-col sm:flex-row justify-between items-center gap-3 ">


          <div className="w-full sm:w-auto flex items-center gap-2">
            <button className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 hover:bg-white text-slate-700 rounded-lg text-sm font-semibold shadow-2xs transition-all">
              <Printer size={16} /> Print Inspection Record
            </button>
            <button className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 border border-slate-300 hover:bg-white text-slate-700 rounded-lg text-sm font-semibold shadow-2xs transition-all">
              Reset
            </button>
            <button className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-bold shadow-sm transition-all">
              <Save size={16} /> Save Changes
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-300 text-xs">
            <span className="text-slate-700 font-medium">IR NO:</span>
            <span className="font-mono font-bold text-indigo-400">1001</span>
          </div>

          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-300 text-xs">
            <Calendar size={14} className="text-slate-400" />
            <span className="text-slate-700">02/07/2026</span>
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={`text-xs font-bold px-3 py-1.5 rounded-lg border focus:outline-none transition-all ${status === 'OPEN' ? 'bg-white text-amber-400 border-amber-500/30' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
              }`}
          >
            <option value="OPEN" className="bg-slate-900 text-amber-400">● OPEN</option>
            <option value="CLOSED" className="bg-slate-900 text-emerald-400">● CLOSED</option>
          </select>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-6 p-6  w-full mx-auto overflow-y-auto">

        <div className="xl:col-span-5 space-y-6">

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <FileText size={16} className="text-indigo-600" />
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Contract Reference Info</h2>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Contract No</label>
                <input type="text" defaultValue="nov 270001" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Registered Tenant Name</label>
                <input type="text" defaultValue="BRIJESH T. C." className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-800" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Lease From Date</label>
                  <input type="text" defaultValue="01/01/2025" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-600" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Lease End Date</label>
                  <input type="text" defaultValue="31/12/2025" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-5">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <Home size={16} className="text-indigo-600" />
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Room Allocation Metrics</h2>
            </div>

            <div className="p-4 bg-slate-50/70 border border-slate-200 rounded-lg space-y-3">
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-indigo-50 text-indigo-700">Present Room/Studio Logs</span>
              <div className="grid grid-cols-4 gap-3 items-end">
                <div className="col-span-3">
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Rooms Info Descriptions</label>
                  <textarea rows={2} placeholder="Enter physical configuration comments..." className="w-full p-2 bg-white border border-slate-200 rounded-md text-xs focus:ring-1 focus:ring-indigo-500" />
                </div>
                <div className="col-span-1">
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Count</label>
                  <input type="number" defaultValue={1} className="w-full p-2 bg-white border border-slate-200 rounded-md text-xs text-center font-bold text-slate-700" />
                </div>
              </div>
            </div>

            <div className="p-4 bg-rose-50/30 border border-rose-100 rounded-lg space-y-3">
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-rose-50 text-rose-700">Vacating Room/Studio Logs</span>
              <div className="grid grid-cols-4 gap-3 items-end">
                <div className="col-span-3">
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Vacating Audit Notes</label>
                  <textarea rows={2} placeholder="Identify damage states upon checkout..." className="w-full p-2 bg-white border border-slate-200 rounded-md text-xs focus:ring-1 focus:ring-rose-500" />
                </div>
                <div className="col-span-1">
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Count</label>
                  <input type="number" defaultValue={1} className="w-full p-2 bg-white border border-slate-200 rounded-md text-xs text-center font-bold text-slate-700" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <AlertTriangle size={16} className="text-indigo-600" />
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Estimated Repair Valuation</h2>
            </div>

            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="col-span-1 relative">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Repair Cost</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-xs font-bold text-slate-400">AED</span>
                  <input type="text" placeholder="0.00" className="w-full pl-11 pr-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800 font-mono font-bold" />
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Specify Outstandings / Dues If Any</label>
                <input type="text" placeholder="Pending utilities, fine adjustments..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN: Structural Checklist Matched with Media Uploader Matrix */}
        <div className="xl:col-span-7 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-sm tracking-wide uppercase">Present Structural Checklist & Evidence Capture</h3>
            {/* <span className="text-[11px] font-medium text-slate-500 bg-slate-200/60 px-2 py-0.5 rounded-full">6 Control Parameters</span> */}
          </div>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto max-h-[720px]">
            {areas.map((area) => (
              <div key={area.id} className="group p-4 rounded-xl border border-slate-100 bg-slate-50/40 hover:bg-slate-50 hover:border-slate-200 transition-all space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <label className="text-xs font-bold text-slate-800 uppercase tracking-wide">{area.label}</label>

                  {/* Digital Capture Tool Strip */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleMediaUpload(area.id, 'image')}
                      className="inline-flex items-center gap-1 text-[11px] font-medium bg-white hover:bg-indigo-50 text-slate-600 hover:text-indigo-600 border border-slate-200 px-2.5 py-1 rounded-md shadow-2xs transition-colors"
                    >
                      <Camera size={13} /> Snap Photo
                    </button>
                    <button
                      onClick={() => handleMediaUpload(area.id, 'video')}
                      className="inline-flex items-center gap-1 text-[11px] font-medium bg-white hover:bg-rose-50 text-slate-600 hover:text-rose-600 border border-slate-200 px-2.5 py-1 rounded-md shadow-2xs transition-colors"
                    >
                      <Video size={13} /> Record Video
                    </button>
                    <label className="inline-flex items-center gap-1 text-[11px] font-medium bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded-md shadow-2xs cursor-pointer transition-colors">
                      <Upload size={13} />
                      <input
                        type="file"
                        accept="image/*,video/*"
                        className="hidden"
                        onChange={(e) => handleMediaUpload(area.id, e.target.files[0]?.type.includes('video') ? 'video' : 'image')}
                      />
                      Upload
                    </label>
                  </div>
                </div>

                <textarea
                  rows={2}
                  placeholder={`Document individual findings regarding the ${area.id.toLowerCase()} inventory condition...`}
                  className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-xs shadow-3xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />

                {/* Media Inline Preview Bar */}
                {uploadedMedia[area.id].length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {uploadedMedia[area.id].map((media) => (
                      <div key={media.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-slate-200 bg-slate-900 group/item shadow-sm">
                        {media.type === 'video' ? (
                          <div className="w-full h-full flex items-center justify-center bg-slate-800 text-white">
                            <Video size={16} className="text-rose-400" />
                          </div>
                        ) : (
                          <img src={media.url} alt="Preview" className="w-full h-full object-cover opacity-80 group-hover/item:opacity-100 transition-opacity" />
                        )}
                        <button
                          onClick={() => removeMedia(area.id, media.id)}
                          className="absolute top-0.5 right-0.5 p-0.5 bg-rose-600 text-white rounded-full opacity-0 group-hover/item:opacity-100 transition-opacity"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>



    </div>
  );
}

